import { ReactNode } from 'react';
import { Trans } from '@lingui/react/macro';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { routeForLearningHub } from '@watershed/shared-universal/dashboardRoutes';
import CollapsibleBlock from '@watershed/ui-core/components/CollapsibleBlock';
import TextLink from '@watershed/ui-core/components/TextLink';
import Well from '@watershed/ui-core/components/Well';

function CollapsibleQuestion({
  question,
  answer,
  initiallyExpanded = false,
}: {
  question: ReactNode;
  answer: ReactNode;
  initiallyExpanded?: boolean;
}) {
  return (
    <CollapsibleBlock
      title={question}
      typographyProps={{ variant: 'body1', flexGrow: 1 }}
      initiallyExpanded={initiallyExpanded}
      isIconOnRight
    >
      <Typography variant="body2">{answer}</Typography>
    </CollapsibleBlock>
  );
}

type FAQItem = {
  question: ReactNode;
  answer: ReactNode;
};

const FAQ_ITEMS: Array<FAQItem> = [
  {
    question: <Trans>What should I include in a support case?</Trans>,
    answer: (
      <Trans>
        To aid our support team in reproducing and investigating product
        behaviors, please include screenshots and/or links to relevant pages.
        Additionally, specifying the names of measurements, upload tasks,
        footprints, and files that you reference is helpful to our team.
      </Trans>
    ),
  },
  {
    question: (
      <Trans>
        We are working on a tight deadline, how can I make sure we reach a
        timely resolution?
      </Trans>
    ),
    answer: (
      <Trans>
        Most importantly, please let us know about any deadlines that are
        relevant to a support ticket. We will do our best to help you meet them!
        Additionally, for urgent requests, selecting the 'Critical' case
        priority will help us to prioritize your case.
      </Trans>
    ),
  },
  {
    question: (
      <Trans>
        Where can I find basic information about how Watershed works, and how to
        upload data?
      </Trans>
    ),
    answer: (
      <Trans>
        You can find this information in the{' '}
        <TextLink
          href={routeForLearningHub({
            from: 'SupportCaseFAQ',
          })}
        >
          Watershed learning hub.
        </TextLink>{' '}
      </Trans>
    ),
  },
  {
    question: (
      <Trans>
        I have several questions. Should I batch them in one support ticket, or
        send them all separately?
      </Trans>
    ),
    answer: (
      <Trans>
        We recommend sending batches of questions in one support ticket. This
        enables our team to work more smoothly with other Watershed teams to
        reach a timely resolution.
      </Trans>
    ),
  },
];

function SupportCaseQuestions() {
  return (
    <Stack gap={2}>
      {FAQ_ITEMS.map((item, index) => (
        <CollapsibleQuestion
          key={index}
          question={item.question}
          answer={item.answer}
          initiallyExpanded={index === 0}
        />
      ))}
    </Stack>
  );
}

export function SupportCaseFAQ() {
  return (
    <Well>
      <Well.Header
        title={
          <Trans context="Header for FAQs">Frequently asked questions</Trans>
        }
        titleVariant="h4"
      />
      <Well.Content>
        <SupportCaseQuestions />
      </Well.Content>
    </Well>
  );
}
export function SupportCaseFAQCollapsible() {
  return (
    <Stack gap={1}>
      <Typography variant="h4">
        <Trans context="Title for section on tips for writing a good support case">
          Tips for faster resolution on your support case
        </Trans>
      </Typography>
      <SupportCaseQuestions />
    </Stack>
  );
}
